import { Box, Button, ButtonProps, CircularProgress, SxProps, Theme } from "@mui/material"
import { PropsWithChildren, forwardRef, Ref } from "react"

interface Props extends ButtonProps {
    bgColor?: string
    loading?: boolean
    target?: string
}

export const LoadingButton = forwardRef(function CustomButton(
    {
        bgColor,
        loading,
        sx = {},
        disabled,
        href,
        target,
        children,
        ...props
    }: PropsWithChildren<Props>,
    ref: Ref<HTMLButtonElement>
) {
    const { color } = props
    const linkProps = href ? { href, target } : {}

    const loadingStyles = {
        // If the button is loading, we want to keep the original color for background
        "&.Mui-disabled": {
            backgroundColor: color === "inherit" ? "inherit" : bgColor || "primary",
        },
        // icon does not go away when parent span opacity is 0
        "& .MuiButton-icon": {
            opacity: loading ? 0 : 1,
        },
    }

    const buttonStyles = {
        ...styles.button,
        ...(loading && loadingStyles),
        ...sx,
    } as SxProps<Theme>

    return (
        <Button
            ref={ref}
            sx={buttonStyles}
            disabled={loading || disabled}
            {...linkProps}
            {...props}
        >
            {loading && <CircularProgress size={20} color="inherit" sx={styles.spinner} />}
            <Box component="span" sx={{ opacity: loading ? 0 : 1 }}>
                {children}
            </Box>
        </Button>
    )
})
LoadingButton.displayName = "LoadingButton"

const styles: Record<string, SxProps<Theme>> = {
    button: {
        position: "relative",
        transition: "all 0.2s ease-in-out",
    },
    spinner: {
        position: "absolute",
        transform: "translate(-50%, -50%)",
    },
}
